import { useCallback, useEffect, useState } from "react";
import AWS from "aws-sdk";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import truncate from "lodash/truncate";
import isEmpty from "lodash/isEmpty";
import { selectApplications } from "../../../../pages/Applications";
import { selectUser } from "../../../../pages/UserProfile";
import {
  selectSelectedUser,
  setSelectedUser,
  updateUser,
  fetchUsers,
  selectUsersFilters,
  inviteUser,
  fetchInvitations,
} from "../../../../pages/Users";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { Modal } from "../../../molecules";
import { MODAL_SIZE } from "../modals.constants";
import {
  EMPTY_STRING,
  REGEX,
  API_RESPONSES,
  EMPTY_ARRAY,
  USER_ROLES,
  ROUTES,
  CACHE_KEYS,
  VALIDATION_MESSAGES,
  ALERT_MESSAGES,
} from "../../../../constants";
import { appIcon, plusIcon, whiteTickIcon } from "../../../../assets/icons";
import { setAlert } from "../../Alert";
import Spinner, { SPINNER_NAMES } from "../../Spinner";

const { ORGANIZATION } = CACHE_KEYS;
const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const { setSelectedModal } = modalActions;
const {
  REACT_APP_AWS_REGION,
  REACT_APP_AWS_ACCESS_KEY_ID,
  REACT_APP_AWS_SECRET_ACCESS_KEY,
  REACT_APP_USER_POOL_ID,
} = process.env;
const { SUCCESS } = API_RESPONSES;
const { UPDATE_USER, INVITE_USER } = SPINNER_NAMES;
const { ADMIN, USER } = USER_ROLES;
const applicationUserRoles = [
  {
    label: "Admin",
    value: "admin",
  },
  {
    label: "Customer",
    value: "customer",
  },
];
const userRoles = [ADMIN, USER];
const { INVITATIONS } = ROUTES;

const cognito = new AWS.CognitoIdentityServiceProvider({
  region: REACT_APP_AWS_REGION,
  accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY,
});

const {
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  USER_REQUIRED,
  INVALID_EMAIL,
  EMAIL_REQUIRED,
  ENTER_DIFFERENT_EMAIL,
  MIN_CHAR_LENGTH,
} = VALIDATION_MESSAGES;

const { EMAIL_SUCCESSFULLY_SENT } = ALERT_MESSAGES;

const ManageUserModal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userAdmin = useSelector(selectUser);
  const selectedUser = useSelector(selectSelectedUser);
  const usersFilters = useSelector(selectUsersFilters);
  const { data: applications } = useSelector(selectApplications);

  const DEFAULT_VALUE = {
    name: EMPTY_STRING,
    email: EMPTY_STRING,
    role: ADMIN,
    applications: EMPTY_ARRAY,
  };
  const [apps, setApps] = useState(EMPTY_ARRAY);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [initialValues, setInitialValues] = useState(DEFAULT_VALUE);
  const isExistingUser = selectedUser?.name || EMPTY_STRING;
  const organizations = JSON.parse(sessionStorage.getItem(ORGANIZATION));
  const organizationId = organizations.id;

  useEffect(() => {
    if (applications) setApps(applications);
  }, [applications]);

  useEffect(() => {
    if (selectedUser) {
      const applications = apps.map(({ id, name, appLogoUrl }) => {
        const application = selectedUser?.userApplicationAssociations.find(
          (app) => app.appId === id
        );
        const isSelected = Boolean(application);
        const userRole = application?.userRole || ADMIN;
        return { appId: id, appName: name, userRole, isSelected, appLogoUrl };
      });
      const { name, email, role } = selectedUser;
      setInitialValues({ name, email, role, applications });
    }
  }, [apps, selectedUser]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAIN)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(USER_REQUIRED),
    email: Yup.string(INVALID_EMAIL)
      .email(INVALID_EMAIL)
      .required(EMAIL_REQUIRED)
      .notOneOf([userAdmin.email], ENTER_DIFFERENT_EMAIL),
  });

  const handleClose = useCallback(() => {
    dispatch(setSelectedUser(null));
    dispatch(setSelectedModal(null));
  }, [dispatch]);

  const handleOnSubmit = useCallback(
    async (values) => {
      const selectedApplications = values.applications.reduce(
        (selectedApplications, { appId, userRole, isSelected }) =>
          isSelected
            ? [
                ...selectedApplications,
                { appId, userId: selectedUser?.id, userRole },
              ]
            : selectedApplications,
        []
      );

      const updatedValues = {
        ...values,
        sub: uuid(),
        profilePicUrl: EMPTY_STRING,
        applications: selectedApplications,
      };
      const user = {
        ...updatedValues,
        organizationId: organizationId,
        invitedBy: userAdmin.email,
      };

      dispatch(
        isExistingUser
          ? updateUser({
              user: updatedValues,
              id: values.id,
            })
          : inviteUser(user)
      ).then(async (res) => {
        dispatch(fetchUsers(usersFilters));
        if (res.payload) {
          !isExistingUser &&
            dispatch(
              setAlert({
                message: EMAIL_SUCCESSFULLY_SENT,
                type: SUCCESS,
              })
            );
          dispatch(fetchInvitations({ organizationId }));
          handleClose();
          !isExistingUser && navigate(INVITATIONS);
        } else {
          !isExistingUser &&
            (await cognito
              .adminDeleteUser({
                UserPoolId: REACT_APP_USER_POOL_ID,
                Username: values.email,
              })
              .promise());
        }
      });
    },
    [
      dispatch,
      handleClose,
      navigate,
      userAdmin,
      isExistingUser,
      usersFilters,
      organizationId,
      selectedUser,
    ]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        handleReset,
        touched,
        isValid,
        errors,
        setFieldValue,
        dirty,
      }) => {
        const footer = (
          <div className="p-6 space-x-1 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn btn-primary-light"
              type="button"
              onClick={() => {
                handleReset();
                dispatch(setSelectedModal(null));
                dispatch(setSelectedUser(null));
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={!isValid || isSpinnerActive || !dirty}
              onClick={handleSubmit}
            >
              <Spinner
                name={
                  isExistingUser ? UPDATE_USER : `${INVITE_USER}${values.email}`
                }
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {isExistingUser ? "Update User" : "Invite User"}
              </Spinner>
            </button>
          </div>
        );

        return (
          <Modal
            title={isExistingUser ? "Manage User" : "Invite User"}
            subtitle={`${isExistingUser ? "Update" : "Configure"}
            the user settings.`}
            size={MODAL_SIZE.MD}
            handleReset={handleReset}
            handleClose={handleClose}
            footer={footer}
          >
            <Form>
              <div className="modal-body p-6">
                <div className="space-y-4 mb-4">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      User Name
                    </label>
                    <Field
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-gray-400 bg-gray-700 p-2 w-full text-white":
                          isExistingUser,
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="User Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isExistingUser}
                    />
                    {touched.name && errors.name && (
                      <div className="font-medium text-red-500 text-sm absolute">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Email Address
                    </label>
                    <Field
                      type="text"
                      className={classNames("form-control border-2", {
                        "border-gray-400 bg-gray-700 p-2 w-full text-white":
                          isExistingUser,
                        "border-red-600": touched.email && errors.email,
                      })}
                      placeholder="Email Address"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isExistingUser}
                    />
                    {touched.email && errors.email && (
                      <div className="font-medium text-red-500 text-sm absolute">
                        {errors.email}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      User Role
                    </label>
                    <Field
                      as="select"
                      className="form-control capitalize"
                      name="role"
                      value={values.role}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option disabled value={EMPTY_STRING}>
                        Select A User Role
                      </option>
                      {userRoles.map((role) => (
                        <option className="capitalize" key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
                <div>
                  {!isEmpty(values.applications) && (
                    <div>
                      <label className="text-sm font-medium text-gray-50 block mb-1">
                        Application Access
                      </label>
                      <div className="border border-gray-500 rounded-lg  px-3 sm:px-4 max-h-56 overflow-auto">
                        <ul className="divide-y divide-gray-500">
                          {values.applications.map(
                            (
                              {
                                appId,
                                appName,
                                isSelected,
                                appLogoUrl,
                                userRole,
                              },
                              index
                            ) => (
                              <li
                                className="flex items-center py-4"
                                key={appId}
                              >
                                <div className="icon w-9 h-9 rounded-full sm:grid place-items-center bg-emerald-500 mr-3 hidden">
                                  <img
                                    src={appLogoUrl || appIcon}
                                    alt="app-icon"
                                  />
                                </div>
                                <h6 className="text-sm text-gray-200 font-bold w-7">
                                  {isSelected
                                    ? truncate(appName, { length: 19 })
                                    : appName}
                                </h6>
                                {isSelected && (
                                  <Field
                                    as="select"
                                    className="bg-slate-800 ml-auto text-gray-200 rounded-md text-sm mr-1 cursor-pointer"
                                    name={`applications[${index}].userRole]`}
                                    value={userRole}
                                  >
                                    {applicationUserRoles.map(
                                      ({ label, value }) => (
                                        <option value={value} key={value}>
                                          {label}
                                        </option>
                                      )
                                    )}
                                  </Field>
                                )}
                                <button
                                  className={classNames(
                                    "h-[38px] py-2 px-4 bg-gray-100 rounded-3xl flex items-center ml-auto",
                                    {
                                      "bg-indigo-600": isSelected,
                                    }
                                  )}
                                  type="button"
                                  onClick={() =>
                                    setFieldValue(
                                      `applications[${index}].isSelected`,
                                      !isSelected
                                    )
                                  }
                                >
                                  <img
                                    src={isSelected ? whiteTickIcon : plusIcon}
                                    width="20"
                                    className="mr-2"
                                    alt="plus-icon"
                                  />
                                  <span
                                    className={classNames(
                                      "text-sm font-semibold",
                                      {
                                        "text-gray-200": isSelected,
                                      }
                                    )}
                                  >
                                    {isSelected ? "Added" : "Add"}
                                  </span>
                                </button>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default ManageUserModal;
